/**
 * TEMPORARY DEMO SESSION PROVIDER
 * 
 * This provider simulates user login/logout for demonstration purposes only.
 * It allows testing of localStorage persistence by emulating a user session.
 * 
 * ⚠️ THIS IS NOT FOR PRODUCTION USE
 * This will be removed when proper authentication is implemented.
 */

import React, { createContext, useContext, useState, useEffect } from 'react';

interface DemoSessionContextType {
  isLoggedIn: boolean;
  userName: string;
  userInitials: string;
  login: () => void;
  logout: () => void;
}

const DemoSessionContext = createContext<DemoSessionContextType | undefined>(undefined);

const DEMO_SESSION_KEY = 'demo-session-active';
const DEMO_USER_NAME = 'Alice Johnson';
const DEMO_USER_INITIALS = 'AJ';

export function DemoSessionProvider({ children }: { children: React.ReactNode }) {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(() => {
    // Check if there's an active session in localStorage
    const sessionActive = localStorage.getItem(DEMO_SESSION_KEY);
    return sessionActive === 'true';
  });

  const login = () => {
    localStorage.setItem(DEMO_SESSION_KEY, 'true');
    setIsLoggedIn(true);
  };

  const logout = () => {
    // Clear the demo session
    localStorage.removeItem(DEMO_SESSION_KEY);
    setIsLoggedIn(false);
    
    // Note: We intentionally DON'T clear user preferences here
    // This simulates a real logout where preferences persist on the server
  };

  // Initialize session on mount if not set
  useEffect(() => {
    if (localStorage.getItem(DEMO_SESSION_KEY) === null) {
      localStorage.setItem(DEMO_SESSION_KEY, 'true');
      setIsLoggedIn(true);
    }
  }, []);

  return (
    <DemoSessionContext.Provider
      value={{
        isLoggedIn,
        userName: DEMO_USER_NAME,
        userInitials: DEMO_USER_INITIALS,
        login,
        logout,
      }}
    >
      {children}
    </DemoSessionContext.Provider>
  );
}

export function useDemoSession() {
  const context = useContext(DemoSessionContext);
  if (context === undefined) {
    throw new Error('useDemoSession must be used within a DemoSessionProvider');
  }
  return context;
}
